import { 
  Bell, Search, ChevronLeft, LogOut, User, Shield, 
  Inbox, Swords, MessageSquare, Building2, Settings, PlusCircle, Wallet, Calendar,
  Users, Heart, GraduationCap, CreditCard, MapPin
} from 'lucide-react';
import logo from '@/assets/logo.png';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { useCourtOwnerRole } from '@/hooks/useCourtOwnerRole';
import { useCoachRole } from '@/hooks/useCoachRole';
import { useConversations } from '@/hooks/useConversations';
import { usePendingClubInvitationsSummary } from '@/hooks/useClubInvitations';
import { useMyPendingInvitations } from '@/hooks/useMatchInvitations';
import { useMyTeamPendingInvitations } from '@/hooks/useMatchTeamInvitations';
import { useCreatorSpectatorRequestsSummary } from '@/hooks/useMySpectatorRequests';
import { useInvitationsSeen } from '@/hooks/useInvitationsSeen';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GlobalSearchDialog } from '@/components/GlobalSearchDialog';
import { useState } from 'react';

interface HeaderProps {
  title?: string;
  showSearch?: boolean;
  showNotification?: boolean;
  showBack?: boolean;
  className?: string;
}

export function Header({ title, showSearch = true, showNotification = true, showBack = false, className }: HeaderProps) {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { unreadCount } = useNotifications();
  const { role: adminRole } = useAdminPermissions();
  const { isCourtOwner } = useCourtOwnerRole();
  const { data: isCoach } = useCoachRole();
  const { data: conversations } = useConversations();
  const { data: clubInvitationsSummary } = usePendingClubInvitationsSummary();
  const { data: pendingMatchInvitations = [] } = useMyPendingInvitations();
  const { data: pendingTeamMatchInvitations = [] } = useMyTeamPendingInvitations();
  const { data: spectatorRequestsSummary } = useCreatorSpectatorRequestsSummary();
  const { isUnseen } = useInvitationsSeen();

  const unreadMessageCount = conversations?.reduce((acc, conv) => acc + (conv.unreadCount || 0), 0) || 0;

  // Only count invitations / requests created AFTER the user last viewed
  // the list. This way the badge auto-clears once the user has "seen" them,
  // without forcing them to accept/decline. Pending records are unchanged.
  const unseenMatchInv = pendingMatchInvitations.filter(
    (i) => isUnseen((i as { created_at?: string }).created_at)
  ).length;
  const unseenTeamInv = pendingTeamMatchInvitations.filter(
    (i) => isUnseen(i.created_at)
  ).length;
  const unseenSpectator =
    spectatorRequestsSummary && isUnseen(spectatorRequestsSummary.latestCreatedAt)
      ? spectatorRequestsSummary.count
      : 0;
  const unseenClub =
    clubInvitationsSummary && isUnseen(clubInvitationsSummary.latestCreatedAt)
      ? clubInvitationsSummary.count
      : 0;

  const totalRequestsCount =
    unseenMatchInv + unseenTeamInv + unseenSpectator + unseenClub;
  
  const [searchOpen, setSearchOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <header className={cn(
      'sticky top-0 z-40 bg-card/95 backdrop-blur-lg border-b border-accent/20 safe-area-top',
      className
    )}>
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-2">
          {showBack && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-muted-foreground hover:text-foreground hover:bg-accent/10 -ml-2"
              onClick={() => navigate(-1)}
              aria-label="Quay lại"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          {title ? (
            <h1 className="text-lg font-bold text-foreground">{title}</h1>
          ) : (
            <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => navigate('/')}>
              <img src={logo} alt="sportrank" width={32} height={32} fetchPriority="high" decoding="async" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-lg text-foreground tracking-tight">
                Sport<span className="text-accent">Rank</span>
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {showSearch && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-muted-foreground hover:text-accent hover:bg-accent/10"
              aria-label="Tìm kiếm"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="w-5 h-5" />
            </Button>
          )}
          {showNotification && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-muted-foreground hover:text-accent hover:bg-accent/10 relative"
              onClick={() => navigate('/notifications')}
              aria-label={unreadCount > 0 ? `Thông báo (${unreadCount} chưa đọc)` : "Thông báo"}
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-destructive rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1 animate-pulse">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
          )}
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full ml-1 relative" aria-label="Menu tài khoản">
                  <Avatar className="w-8 h-8 ring-2 ring-accent/30">
                    <AvatarImage src={profile?.avatar || user.user_metadata?.avatar_url} className="object-cover" />
                    <AvatarFallback className="bg-accent text-accent-foreground text-xs font-bold">
                      {profile?.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {(unreadMessageCount + totalRequestsCount) > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] bg-accent rounded-full text-[10px] font-bold text-accent-foreground flex items-center justify-center px-1 ring-2 ring-card">
                      {(unreadMessageCount + totalRequestsCount) > 9 ? '9+' : (unreadMessageCount + totalRequestsCount)}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 bg-card border-border max-h-[80vh] overflow-y-auto">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-semibold leading-none text-foreground">{profile?.name || 'Người dùng'}</p>
                    <p className="text-xs leading-none text-muted-foreground truncate">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-border" />

                {/* Group 1: Personal */}
                <DropdownMenuItem onClick={() => navigate(`/players/${profile?.id}`)} className="focus:bg-accent/10 focus:text-accent">
                  <User className="w-4 h-4 mr-2" />
                  Hồ sơ cá nhân
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/courts/my-courts')} className="focus:bg-accent/10 focus:text-accent">
                  <MapPin className="w-4 h-4 mr-2" />
                  Sân của tôi
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/chat')} className="focus:bg-accent/10 focus:text-accent">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  <span className="flex-1">Tin nhắn</span>
                  {unreadMessageCount > 0 && (
                    <span className="ml-2 min-w-[18px] h-[18px] bg-accent rounded-full text-[10px] font-bold text-accent-foreground flex items-center justify-center px-1">
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-requests')} className="focus:bg-accent/10 focus:text-accent">
                  <Inbox className="w-4 h-4 mr-2" />
                  <span className="flex-1">Lời mời & yêu cầu</span>
                  {totalRequestsCount > 0 && (
                    <span className="ml-2 min-w-[18px] h-[18px] bg-destructive rounded-full text-[10px] font-bold text-destructive-foreground flex items-center justify-center px-1">
                      {totalRequestsCount > 99 ? '99+' : totalRequestsCount}
                    </span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile/training-schedule')} className="focus:bg-accent/10 focus:text-accent">
                  <Calendar className="w-4 h-4 mr-2" />
                  Lịch của tôi
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/matches/manage')} className="focus:bg-accent/10 focus:text-accent">
                  <Swords className="w-4 h-4 mr-2" />
                  Quản lý trận đấu
                </DropdownMenuItem>

                {/* Group 2: Community */}
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuItem onClick={() => navigate('/clubs')} className="focus:bg-accent/10 focus:text-accent">
                  <Building2 className="w-4 h-4 mr-2" />
                  CLB của tôi
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile/teams')} className="focus:bg-accent/10 focus:text-accent">
                  <Users className="w-4 h-4 mr-2" />
                  Đội của tôi
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile/following')} className="focus:bg-accent/10 focus:text-accent">
                  <Heart className="w-4 h-4 mr-2" />
                  Theo dõi
                </DropdownMenuItem>

                {/* Group 3: Finance */}
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuItem onClick={() => navigate('/payment-history')} className="focus:bg-accent/10 focus:text-accent">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Lịch sử giao dịch
                </DropdownMenuItem>
                {(isCourtOwner || isCoach) && (
                  <DropdownMenuItem onClick={() => navigate('/earnings')} className="focus:bg-accent/10 focus:text-accent">
                    <Wallet className="w-4 h-4 mr-2" />
                    Thu nhập của tôi
                  </DropdownMenuItem>
                )}

                {/* Group 4: Role-based Management */}
                {(isCourtOwner || isCoach) && (
                  <>
                    <DropdownMenuSeparator className="bg-border" />
                    {isCourtOwner && (
                      <DropdownMenuItem onClick={() => navigate('/courts/new')} className="focus:bg-accent/10 focus:text-accent">
                        <PlusCircle className="w-4 h-4 mr-2" />
                        Tạo sân mới
                      </DropdownMenuItem>
                    )}
                    {isCoach && (
                      <DropdownMenuItem onClick={() => navigate('/my-classes')} className="focus:bg-accent/10 focus:text-accent">
                        <GraduationCap className="w-4 h-4 mr-2" />
                        Lớp học của tôi
                      </DropdownMenuItem>
                    )}
                  </>
                )}

                {/* Group 5: System */}
                <DropdownMenuSeparator className="bg-border" />
                {adminRole && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="focus:bg-accent/10 focus:text-accent">
                    <Shield className="w-4 h-4 mr-2" />
                    {adminRole === 'admin' ? 'Quản trị hệ thống' : 'Quản trị (Moderator)'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/settings')} className="focus:bg-accent/10 focus:text-accent">
                  <Settings className="w-4 h-4 mr-2" />
                  Cài đặt
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive focus:bg-destructive/10">
                  <LogOut className="w-4 h-4 mr-2" />
                  Đăng xuất
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              size="sm" 
              className="font-semibold ml-2"
              style={{ background: 'var(--gradient-cta)', boxShadow: 'var(--shadow-neon)' }}
              onClick={() => navigate('/auth')}
            >
              Đăng nhập
            </Button>
          )}
        </div>
      </div>
      <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
